import type { Locale } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import intervalPlural from 'i18next-intervalplural-postprocessor';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';

// Every locales/<code>.json is a supported language. English ships in the main bundle; the rest load on demand.
const lazyLocales = import.meta.glob(['./locales/*.json', '!./locales/en.json']);
const localePath = (language: string) => `./locales/${language}.json`;

export const bundledLanguages = [
  'en',
  ...Object.keys(lazyLocales).map(path => path.replace('./locales/', '').replace('.json', '')),
];

// date-fns month and weekday names, loaded together with each language's translation file
const dateFnsLocales = import.meta.glob<Locale>('/node_modules/date-fns/esm/locale/*/index.js', { import: 'default' });
const dateFnsLocalePath = (language: string) => `/node_modules/date-fns/esm/locale/${language}/index.js`;
const loadedDateLocales = new Map<string, Locale>();
let currentDateLocale: Locale = enUS;

const loadLanguage = async (language: string) => {
  const loadDateLocale = dateFnsLocales[dateFnsLocalePath(language)] ?? dateFnsLocales[dateFnsLocalePath(language.split('-')[0])];
  const [translation, dateLocale] = await Promise.all([lazyLocales[localePath(language)](), loadDateLocale?.()]);
  if (dateLocale) loadedDateLocales.set(language, dateLocale);
  return translation;
};

export const dateLocale = () => currentDateLocale;

// the app's language for Intl number formatting; English variants all format numbers alike
export const numberLocale = () => i18n.resolvedLanguage ?? 'en';

// the call site's own locale for English, otherwise the user's language
export const displayLocale = (english?: string) =>
  i18n.resolvedLanguage && i18n.resolvedLanguage !== 'en' ? i18n.resolvedLanguage : english;

i18n.on('languageChanged', language => {
  document.documentElement.setAttribute('lang', language);
  currentDateLocale = loadedDateLocales.get(language) ?? enUS;
});

let initialized: Promise<unknown> = Promise.resolve();
let requestedLanguage: string | undefined;

// i18next lets a slower, earlier language load finish last and win, so wait for the detected language before switching.
export const changeLanguage = async (language: string) => {
  requestedLanguage = language;
  await initialized;
  await i18n.changeLanguage(language);
};

// The account's language wins over this browser's, except on a page opened with ?lng=:
// the mobile apps open KYC that way, and it must match the app's language.
export const applyAccountLanguage = (language?: string) => {
  if (language && !new URLSearchParams(window.location.search).has('lng')) void changeLanguage(language);
};

// the language last chosen, even while its translation file is still loading
export const selectedLanguage = () => requestedLanguage ?? i18n.resolvedLanguage;

const i18nInit = () => {
  return (initialized = i18n
    .use(LanguageDetector)
    .use(resourcesToBackend(loadLanguage))
    .use(initReactI18next)
    // reference: https://www.i18next.com/translation-function/plurals#ordinal-plurals
    .use(intervalPlural)
    .init({
      resources: { en: { translation: en } },
      partialBundledLanguages: true,
      supportedLngs: bundledLanguages,
      fallbackLng: 'en',
      // ?lng= is how the mobile apps hand their language to the KYC WebView.
      detection: { order: ['querystring', 'localStorage', 'navigator'], lookupQuerystring: 'lng', caches: ['localStorage'] },
      // Keys are the English text itself, so they read naturally in code and give translators (human or LLM) the meaning.
      // Editing that English text changes the key: rename it in every locales/*.json too, or those languages silently fall back to English.
      // Use a descriptive ID-style key only for long or ambiguous strings, e.g. 'Close position' rather than 'Close'.
      keySeparator: false,
      nsSeparator: false,
      returnNull: false,
      // a key missing from en.json is also untranslated in every other language; the extractor can't see computed keys
      saveMissing: import.meta.env.DEV && import.meta.env.MODE !== 'test',
      missingKeyHandler: (_languages, _namespace, key, fallbackValue) => {
        // a call with its own defaultValue has chosen its fallback on purpose
        if (key && fallbackValue === key) console.warn(`[i18n] missing key: ${key}`);
      },
      interpolation: {
        escapeValue: false, // react already safes from xss
      },
    }));
};

export default i18nInit;
